import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { downloadAuthenticated } from "../lib/download";
import PageHeader from "../components/PageHeader";
import LoadingState from "../components/LoadingState";
import EmptyState from "../components/EmptyState";
import { toast } from "sonner";
import { Download, FileText } from "lucide-react";

const PERIODS = [
    { key: "daily", label: "Giornaliero" },
    { key: "weekly", label: "Settimanale" },
    { key: "monthly", label: "Mensile" },
];

export default function ReportsPage() {
    const [tab, setTab] = useState("pec");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [aggType, setAggType] = useState("monthly");
    const [generating, setGenerating] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/reports");
            setItems(data.items || []);
        } catch { toast.error("Errore caricamento report"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const download = async (rid) => {
        const item = items.find((r) => r.id === rid);
        const filename = item?.report_id_human ? `${item.report_id_human}.pdf` : `report_${rid}.pdf`;
        await downloadAuthenticated(`/reports/${rid}/download`, filename);
    };

    const generateAggregate = async () => {
        setGenerating(true);
        try {
            await api.post("/reports/aggregate", { type: aggType });
            toast.success("Report aggregato generato");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
        finally { setGenerating(false); }
    };

    const filtered = items.filter(r => tab === "pec" ? r.kind === "pec_single" : r.kind === "aggregate");

    return (
        <div className="space-y-4">
            <PageHeader
                title="Report"
                subtitle="Report PDF singoli e aggregati"
                breadcrumbs={[{ label: "Report" }]}
            />
            <div className="flex gap-2 text-xs">
                <button data-testid="reports-tab-pec" onClick={() => setTab("pec")}
                    className={`px-3 py-1.5 rounded-md ${tab === "pec" ? "bg-[#0B1633] text-white" : "bg-white border border-[#DCE3EE]"}`}>
                    Report singoli PEC
                </button>
                <button data-testid="reports-tab-agg" onClick={() => setTab("aggregate")}
                    className={`px-3 py-1.5 rounded-md ${tab === "aggregate" ? "bg-[#0B1633] text-white" : "bg-white border border-[#DCE3EE]"}`}>
                    Report aggregati
                </button>
            </div>

            {tab === "aggregate" && (
                <div className="bg-white border border-[#DCE3EE] rounded-xl p-4">
                    <h3 className="text-sm font-semibold mb-3">Genera nuovo report aggregato</h3>
                    <div className="flex items-center gap-2">
                        <select data-testid="agg-type-select" value={aggType} onChange={(e) => setAggType(e.target.value)}
                            className="text-xs border border-[#DCE3EE] rounded-md px-2 py-1.5 bg-white">
                            {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                        </select>
                        <button data-testid="agg-generate" onClick={generateAggregate} disabled={generating}
                            className="text-xs px-3 py-1.5 rounded-md bg-[#2563EB] text-white disabled:opacity-50">
                            {generating ? "Generazione…" : "Genera"}
                        </button>
                    </div>
                </div>
            )}

            {loading ? <LoadingState /> : filtered.length === 0 ? (
                <EmptyState title="Nessun report" description={tab === "pec" ? "Generane uno dal dettaglio di una PEC." : "Nessun report aggregato disponibile."} />
            ) : (
                <div className="bg-white border border-[#DCE3EE] rounded-xl overflow-hidden">
                    <table className="w-full text-sm" data-testid="reports-table">
                        <thead className="bg-[#F5F7FB] text-[11px] uppercase text-[#64748B]">
                            <tr>
                                <th className="text-left px-4 py-3">Report ID</th>
                                <th className="text-left px-4 py-3">Tipo</th>
                                <th className="text-left px-4 py-3">Data</th>
                                <th className="text-left px-4 py-3">Dimensione</th>
                                <th className="text-left px-4 py-3">Azioni</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#DCE3EE]">
                            {filtered.map((r) => (
                                <tr key={r.id} data-testid={`report-row-${r.id}`} className="hover:bg-[#F5F7FB]">
                                    <td className="px-4 py-3 text-xs font-mono">{r.report_id_human}</td>
                                    <td className="px-4 py-3 text-xs">{r.kind === "pec_single" ? "Singolo PEC" : r.period_label || "Aggregato"}</td>
                                    <td className="px-4 py-3 text-xs text-[#64748B]">{(r.generated_at || "").slice(0, 16).replace("T", " ")}</td>
                                    <td className="px-4 py-3 text-xs">{Math.round((r.size || 0) / 1024)} KB</td>
                                    <td className="px-4 py-3">
                                        <button data-testid={`report-download-${r.id}`} onClick={() => download(r.id)}
                                            className="inline-flex items-center gap-1 text-xs text-[#2563EB] hover:underline">
                                            <Download className="w-3.5 h-3.5" /> Scarica PDF
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
